import { useTranslation } from 'react-i18next';
import { Icon, Target, Pencil, Plus } from '../../ui/Icon';
import type { BudgetStatusItem, Trip } from '../../../types';
import { BudgetBar, tripPeople, useBudgetLabel } from './budgetHelpers';

interface Props {
  status: BudgetStatusItem[];
  trip: Trip;
  canEdit: boolean;
  onEdit: () => void;
}

// Order limits so the card reads total → daily → categories → people.
const SCOPE_ORDER: Record<string, number> = { TOTAL: 0, DAILY: 1, CATEGORY: 2, PERSON: 3 };

export function BudgetOverview({ status, trip, canEdit, onEdit }: Props) {
  const { t } = useTranslation();
  const label = useBudgetLabel();
  const people = tripPeople(trip);
  const currency = trip.budgetCurrency || 'EUR';

  const sorted = [...status].sort((a, b) =>
    (SCOPE_ORDER[a.scope] - SCOPE_ORDER[b.scope]) || b.used - a.used,
  );

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5">
          <Icon as={Target} size={14} className="text-slate-400" />
          <p className="font-mono text-xs tracking-[0.1em] uppercase text-slate-500 dark:text-slate-400">
            {t('expenses.budget.title')}
          </p>
        </div>
        {canEdit && (
          <button
            onClick={onEdit}
            className="text-slate-400 hover:text-teal-600 transition-colors cursor-pointer"
            title={t('expenses.budget.edit')}
          >
            <Icon as={Pencil} size={14} />
          </button>
        )}
      </div>

      {sorted.length === 0 ? (
        <div className="flex flex-col items-center text-center gap-2 py-4">
          <p className="text-sm text-slate-500 dark:text-slate-400">{t('expenses.budget.emptyDesc')}</p>
          {canEdit && (
            <button onClick={onEdit} className="btn-secondary gap-1.5 mt-1">
              <Icon as={Plus} size={14} />
              {t('expenses.budget.set')}
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          {sorted.map(s => (
            <BudgetBar key={s.id} label={label(s, people)} item={s} currency={currency} />
          ))}
        </div>
      )}
    </div>
  );
}
